import { getApiEndpointErrorMessage, getRequest, postRequest } from '@/utils/apiUtils';
import React from 'react';
import { useToast } from '@/hooks/useToast';
import { ErrorResponse } from '@/types/api/responses/ErrorResponse';
import { ApiEndpointEnum } from '@/enums/ApiEndpointEnum';
import Toast from '@/components/ui/toast/Toast';

const useApiFetch = () => {
  const { toastError } = useToast();
  /**
   * Performs a GET request to the specified API endpoint with provided query parameters.
   *
   * @param {ApiEndpointEnum} endpoint - The API endpoint to send the GET request to.
   * @param {URLSearchParams} params - The query parameters to include in the GET request.
   * @param signal
   * @returns {Promise<any>} - A promise that resolves with the parsed JSON response from the API.
   */
  const get = async (endpoint: ApiEndpointEnum, params?: URLSearchParams, signal?: AbortSignal) => {
    const res = await fetch(getRequest(endpoint, params), { signal });

    if (!res.ok) {
      await handleError(endpoint, res);
    }

    if (res.status === 204) {
      return {};
    }

    return res.json();
  };

  /**
   * Makes a POST request to the specified API endpoint with the given request body.
   *
   * @param {ApiEndpointEnum} endpoint - The API endpoint to make the POST request to.
   * @param {BodyInit | null | undefined} body - The request body to send with the POST request.
   * @returns {Promise<Object>} - A Promise that resolves to the JSON response from the POST request.
   */
  const post = async (endpoint: ApiEndpointEnum, body: BodyInit | null | undefined) => {
    const res = await fetch(postRequest(endpoint, body));

    if (!res.ok) {
      await handleError(endpoint, res);
    }

    return res.json();
  };

  /**
   * Handles error response from API requests.
   *
   * @param {ApiEndpointEnum} endpoint - The API endpoint where the error occurred.
   * @param {Response} res - The response object from the API.
   * @returns {Promise<void>} - Promise that resolves once error handling is done.
   */
  const handleError = async (endpoint: ApiEndpointEnum, res: Response) => {
    const errorResponse = (await res.json()) as ErrorResponse;
    toastError(
      <Toast
        heading={getApiEndpointErrorMessage(endpoint)}
        message={errorResponse.message ?? errorResponse.error ?? 'Ukjent feil'}
      />
    );
    throw new Error(errorResponse.message);
  };

  return {
    get,
    post
  };
};

export default useApiFetch;
